import { type CSSObject } from '@emotion/react'
import {
  Color,
  FocusIndicator,
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  Transition,
  ZIndex,
} from '@opallabs/gem'
import { WithRequiredTheme } from '@opallabs/lapidary'
import { CONTROL_BAR_BLOCK_SIZE } from '../FullControlBar/styles'

type StyleProps = {
  isVisible: boolean
  viewOnly: boolean
}

/**
 * `transition` can’t transition between a numeric value and `auto`. A
 * workaround is to use the `max-` version of the property and a ‘big enough’
 * value instead of `auto`.
 *
 * @see https://stackoverflow.com/a/38643863
 */
const BIG_ENOUGH_EXPANDED_SIZE = Spacing.XL * 10
const COLLAPSED_SIZE = Spacing.L + Spacing.XS / 2
export const versionModalToggle = ({
  isVisible,
  theme,
  viewOnly,
}: WithRequiredTheme & StyleProps): CSSObject => ({
  boxShadow: 'none',
  blockSize: COLLAPSED_SIZE,
  borderRadius: Size.Rounding * 4,
  maxInlineSize: COLLAPSED_SIZE,
  insetBlockEnd:
    (viewOnly || !isVisible ? 0 : CONTROL_BAR_BLOCK_SIZE) + Spacing.M,
  insetInlineEnd: Spacing.L + 2,
  opacity: isVisible ? 1 : 0,
  overflow: 'hidden',
  paddingBlock: Spacing.XS * 0.75,
  paddingInline: Spacing.XS + 1,
  position: 'absolute',
  zIndex: ZIndex.Shroud.Footer,
  ...Transition(['inset-block-end', 'max-inline-size', 'opacity']),
  backgroundColor: Color[theme].Surface.N000,
  ...FocusIndicator[theme],

  '.lapidary-button-icon': {
    marginInlineEnd: 0,
    ...Transition(['margin-inline-end']),
  },
  '.lapidary-button-text': {
    maxInlineSize: 0,
    overflow: 'hidden',
    ...Transition(['max-inline-size']),
  },

  '&:hover': {
    maxInlineSize: BIG_ENOUGH_EXPANDED_SIZE,
    boxShadow: [
      // The `<Button>` focus state uses `FocusIndicator`, which is
      // implemented with `box-shadow`. Using just `Shadows` would remove
      // `FocusIndicator`. Fortunately the hover state is supposed to have
      // an outline, so using `FocusIndicator` here handles that *and* avoids
      // breaking the focus state.
      FocusIndicator[theme].boxShadow,
      Shadow[ShadowElevation.E2].boxShadow,
    ].join(', '),

    '.lapidary-button-icon': {
      marginInlineEnd: Spacing.XS,
    },
    '.lapidary-button-text': {
      maxInlineSize: BIG_ENOUGH_EXPANDED_SIZE,
    },
  },
})
